import { SocketTopics } from "@shared/types";
import {
  ExchangeMapping,
  GetTopicDataFunc,
  SocketPriceArgs,
  TopicReducerHandler,
} from "@shared/services/socket/types";
import { isSymbol } from "@shared/types/utils";

export class ExchangeToSymbolHandler<
  Topic extends SocketTopics.PRICES | SocketTopics.GRAPHS_DATA
> implements TopicReducerHandler<Topic>
{
  private state: ExchangeMapping = {
    exchange_to_symbols: {},
    exchange_to_assets: {},
  };

  getStateExchangeMapping(isAsset?: boolean) {
    if (isAsset) return this.state.exchange_to_assets;
    return this.state.exchange_to_symbols;
  }

  getExchangeMapping(isAsset?: boolean) {
    if (isAsset) return "exchange_to_assets";
    return "exchange_to_symbols";
  }

  add({ exchange, symbol, isAsset }: SocketPriceArgs) {
    let stateExchangeMapping = this.getStateExchangeMapping(isAsset);
    const exchangeMapping = this.getExchangeMapping(isAsset);
    let shouldUpdate = false;

    const isExist = !!stateExchangeMapping[exchange]?.[symbol];
    if (isExist) {
      ++stateExchangeMapping[exchange][symbol];
    } else {
      shouldUpdate = true;
      if (!stateExchangeMapping[exchange]) {
        stateExchangeMapping = {
          ...stateExchangeMapping,
          [exchange]: { [symbol]: 1 },
        };
      } else {
        stateExchangeMapping[exchange] = {
          ...stateExchangeMapping[exchange],
          [symbol]: 1,
        };
      }
    }

    this.state[exchangeMapping] = stateExchangeMapping;
    return shouldUpdate;
  }

  remove({ exchange, symbol, isAsset }: SocketPriceArgs): [boolean, boolean] {
    const stateExchangeMapping = this.getStateExchangeMapping(isAsset);
    const exchangeMapping = this.getExchangeMapping(isAsset);
    const isExist = !!stateExchangeMapping[exchange]?.[symbol];
    let resubscribe = false;
    if (isExist) {
      --stateExchangeMapping[exchange][symbol];
      if (stateExchangeMapping[exchange][symbol] <= 0) {
        resubscribe = true;
        delete stateExchangeMapping[exchange][symbol];
      }
      if (!Object.keys(stateExchangeMapping[exchange]).length) {
        delete stateExchangeMapping[exchange];
      }
      this.state[exchangeMapping] = stateExchangeMapping;
    }

    const unsubscribe =
      Object.keys(this.state.exchange_to_assets).length === 0 &&
      Object.keys(this.state.exchange_to_symbols).length === 0;
    return [!unsubscribe && resubscribe, unsubscribe];
  }

  getState = () => {
    let exchange_to_symbols = {};
    let exchange_to_assets = {};

    for (let exchange in this.state.exchange_to_symbols) {
      const symbolsArray = Object.keys(
        this.state.exchange_to_symbols[exchange]
      );
      exchange_to_symbols = {
        ...exchange_to_symbols,
        [exchange]: symbolsArray,
      };
    }
    for (let exchange in this.state.exchange_to_assets) {
      const symbolsArray = Object.keys(this.state.exchange_to_assets[exchange]);
      exchange_to_assets = { ...exchange_to_assets, [exchange]: symbolsArray };
    }

    return {
      exchange_to_symbols,
      exchange_to_assets,
    };
  };

  getData: GetTopicDataFunc<Topic> = (args, data) => {
    if (isSymbol(args)) return undefined;
    const { exchange, symbol } = args;
    return data?.[exchange]?.[symbol];
  };
}
